import React, { useState, useEffect } from 'react';
import { Guide } from '../../types';
import { guideService } from '../../services/guideService';
import ImageUpload from '../Common/ImageUpload';
import { useToast } from '../../contexts/ToastContext';

interface GuideFormProps {
  guide?: Guide | null;
  onClose: () => void;
  onSubmit: () => void;
}

const GuideForm: React.FC<GuideFormProps> = ({ guide, onClose, onSubmit }) => {
  const { showSuccess, showError } = useToast();

  const emptyForm = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    bio: '',
    experience: '',
    languages: [] as string[],
    specialties: [] as string[],
    rating: 4,
    avatar: '',
    certifications: [] as string[],
    location: '',
    availability: true,
    emergencyContact: { name: '', phone: '', relationship: '' },
    active: true,
    rankingId: '',
  };

  const [formData, setFormData] = useState(emptyForm);
  const [newLanguage, setNewLanguage] = useState('');
  const [newSpecialty, setNewSpecialty] = useState('');
  const [newCertification, setNewCertification] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeSection, setActiveSection] = useState<'personal' | 'professional' | 'skills' | 'emergency'>('personal');
  const [rankings, setRankings] = useState<any[]>([]);

  useEffect(() => {
    const loadRankings = async () => {
      try {
        const data = await guideService.getGuideRankings();
        setRankings(data);
      } catch (err) {
        console.error('Error loading rankings:', err);
      }
    };
    loadRankings();
  }, []);

  useEffect(() => {
    if (guide) {
      setFormData({
        firstName: guide.firstName,
        lastName: guide.lastName,
        email: guide.email,
        phone: guide.phone,
        bio: guide.bio,
        experience: guide.experience,
        languages: guide.languages || [],
        specialties: guide.specialties || [],
        rating: guide.rating,
        avatar: guide.avatar || '',
        certifications: guide.certifications || [],
        location: guide.location,
        availability: guide.availability,
        emergencyContact: (guide as any).emergencyContact || (guide as any).emergency_contact || { name: '', phone: '', relationship: '' },
        active: guide.active,
        rankingId: guide.rankingId || '',
      });
    } else {
      const cached = localStorage.getItem('guide_form_cache');
      if (cached) {
        try { setFormData(prev => ({ ...prev, ...JSON.parse(cached) })); } catch {}
      }
    }
  }, [guide]);

  useEffect(() => {
    if (!guide && (formData.firstName || formData.lastName)) {
      localStorage.setItem('guide_form_cache', JSON.stringify(formData));
    }
  }, [formData, guide]);

  const clearCache = () => localStorage.removeItem('guide_form_cache');

  const validate = () => {
    const e: Record<string, string> = {};
    if (!formData.firstName.trim()) e.firstName = 'Required';
    if (!formData.lastName.trim()) e.lastName = 'Required';
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) e.email = 'Valid email required';
    if (!formData.phone.trim()) e.phone = 'Required';
    if (!formData.bio.trim()) e.bio = 'Required';
    if (!formData.experience.trim()) e.experience = 'Required';
    if (formData.languages.length === 0) e.languages = 'Add at least one language';
    if (formData.specialties.length === 0) e.specialties = 'Add at least one specialty';
    if (!formData.location.trim()) e.location = 'Required';
    if (!formData.emergencyContact.name.trim()) e.emergencyName = 'Required';
    if (!formData.emergencyContact.phone.trim()) e.emergencyPhone = 'Required';
    if (!formData.emergencyContact.relationship.trim()) e.emergencyRelationship = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) {
      showError('Please fill in all required fields', 'Validation Error');
      return;
    }
    setLoading(true);
    try {
      if (guide) {
        await guideService.updateGuide(guide.id, formData);
        showSuccess('Guide updated successfully!', 'Updated');
      } else {
        await guideService.createGuide(formData);
        clearCache();
        showSuccess('Guide created successfully!', 'Created');
      }
      onSubmit();
    } catch (err) {
      showError('Error saving guide. Please try again.', 'Save Error');
    } finally {
      setLoading(false);
    }
  };

  const addTag = (list: string[], value: string, setter: (v: string[]) => void, inputSetter: (v: string) => void) => {
    const trimmed = value.trim();
    if (trimmed && !list.includes(trimmed)) {
      setter([...list, trimmed]);
      inputSetter('');
    }
  };

  const removeTag = (list: string[], value: string, setter: (v: string[]) => void) =>
    setter(list.filter(item => item !== value));

  const sections = [
    { id: 'personal', label: 'Personal', icon: '👤' },
    { id: 'professional', label: 'Professional', icon: '💼' },
    { id: 'skills', label: 'Skills', icon: '🎯' },
    { id: 'emergency', label: 'Emergency', icon: '🚨' },
  ] as const;

  const inputCls = (hasError?: boolean) =>
    `w-full px-4 py-3 rounded-xl border text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500 bg-white ${hasError ? 'border-red-400 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`;

  const labelCls = 'block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5';

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-0 sm:p-4 animate-fade-in">
      <div className="bg-white w-full sm:max-w-3xl sm:rounded-3xl shadow-2xl flex flex-col h-full sm:h-auto max-h-[100dvh] sm:max-h-[92vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 sm:py-5 border-b border-gray-100 bg-gradient-to-r from-primary-700 to-primary-900 text-white sm:rounded-t-3xl flex-shrink-0">
          <div>
            <h2 className="text-lg sm:text-xl font-bold tracking-tight">
              {guide ? 'Edit Guide' : 'Add New Guide'}
            </h2>
            <p className="text-primary-200 text-sm mt-0.5">
              {guide ? `Editing ${guide.firstName} ${guide.lastName}` : 'Fill in the details below'}
            </p>
          </div>
          <button
            onClick={() => {
              if (!guide && (formData.firstName || formData.lastName) && window.confirm('Discard unsaved changes?')) clearCache();
              onClose();
            }}
            className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Section Tabs */}
        <div className="flex border-b border-gray-100 bg-gray-50 flex-shrink-0 overflow-x-auto scrollbar-hide">
          {sections.map(s => (
            <button
              key={s.id}
              type="button"
              onClick={() => setActiveSection(s.id)}
              className={`flex-1 min-w-[90px] sm:min-w-[100px] py-3 sm:py-4 px-2 text-[10px] sm:text-xs font-bold sm:font-semibold transition-all duration-200 border-b-2 whitespace-nowrap ${
                activeSection === s.id
                  ? 'border-primary-600 text-primary-700 bg-white shadow-sm'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-100/50'
              }`}
            >
              <span className="block sm:inline sm:mr-1 text-sm sm:text-inherit mb-0.5 sm:mb-0">{s.icon}</span>
              {s.label}
            </button>
          ))}
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto bg-gray-50/30">
          <form id="guide-form" onSubmit={handleSubmit} className="h-full">
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">

              {/* ─── PERSONAL ─── */}
              {activeSection === 'personal' && (
                <>
                  <div className="flex flex-col items-center sm:items-start flex-shrink-0">
                    <label className={labelCls}>Profile Photo</label>
                    <div className="w-32 h-32 sm:w-40 sm:h-40 overflow-hidden rounded-2xl border border-gray-100 shadow-sm">
                      <ImageUpload
                        onImageUpload={(url) => setFormData(p => ({ ...p, avatar: url }))}
                        currentImageUrl={formData.avatar}
                        aspectRatio="square"
                        maxSize={5}
                        category="guides"
                        placeholder="Photo"
                        className="w-full h-full"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>First Name *</label>
                      <input type="text" className={inputCls(!!errors.firstName)} value={formData.firstName}
                        onChange={e => setFormData(p => ({ ...p, firstName: e.target.value }))}
                        placeholder="e.g. Allan" />
                      {errors.firstName && <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>Last Name *</label>
                      <input type="text" className={inputCls(!!errors.lastName)} value={formData.lastName}
                        onChange={e => setFormData(p => ({ ...p, lastName: e.target.value }))}
                        placeholder="e.g. Ledama" />
                      {errors.lastName && <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>Email *</label>
                      <input type="email" className={inputCls(!!errors.email)} value={formData.email}
                        onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                        placeholder="guide@example.com" />
                      {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls}>Phone *</label>
                      <input type="tel" className={inputCls(!!errors.phone)} value={formData.phone}
                        onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                        placeholder="+254 7XX XXX XXX" />
                      {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls}>Location *</label>
                      <input type="text" className={inputCls(!!errors.location)} value={formData.location}
                        onChange={e => setFormData(p => ({ ...p, location: e.target.value }))}
                        placeholder="e.g. Narok, Kenya" />
                      {errors.location && <p className="text-red-500 text-xs mt-1">{errors.location}</p>}
                    </div>
                  </div>

                  {/* Status toggles */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    {[
                      { key: 'availability', label: 'Available for tours', emoji: '✅' },
                      { key: 'active', label: 'Active guide', emoji: '🟢' },
                    ].map(({ key, label, emoji }) => (
                      <label key={key} className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 cursor-pointer hover:bg-primary-50 hover:border-primary-300 transition-all group">
                        <input
                          type="checkbox"
                          checked={(formData as any)[key]}
                          onChange={e => setFormData(p => ({ ...p, [key]: e.target.checked }))}
                          className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm font-medium text-gray-700">{emoji} {label}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}

              {/* ─── PROFESSIONAL ─── */}
              {activeSection === 'professional' && (
                <>
                  <div>
                    <label className={labelCls}>Bio *</label>
                    <textarea className={inputCls(!!errors.bio)} value={formData.bio} rows={4}
                      onChange={e => setFormData(p => ({ ...p, bio: e.target.value }))}
                      placeholder="Describe this guide's background, passion, and expertise..." />
                    {errors.bio && <p className="text-red-500 text-xs mt-1">{errors.bio}</p>}
                  </div>
                  <div>
                    <label className={labelCls}>Experience *</label>
                    <textarea className={inputCls(!!errors.experience)} value={formData.experience} rows={3}
                      onChange={e => setFormData(p => ({ ...p, experience: e.target.value }))}
                      placeholder="e.g. 5 years guiding wildlife safaris in Maasai Mara..." />
                    {errors.experience && <p className="text-red-500 text-xs mt-1">{errors.experience}</p>}
                  </div>
                  <div>
                    <label className={labelCls}>Guide Ranking</label>
                    <select
                      className={inputCls()}
                      value={formData.rankingId}
                      onChange={e => setFormData(p => ({ ...p, rankingId: e.target.value }))}
                    >
                      <option value="">Select Ranking (Optional)</option>
                      {rankings.map(r => (
                        <option key={r.id} value={r.id}>{r.name} (${r.dailyRate}/day)</option>
                      ))}
                    </select>
                    <p className="text-[10px] text-gray-400 mt-1 uppercase font-bold tracking-widest">Sets the base rate for this guide</p>
                  </div>
                </>
              )}

              {/* ─── SKILLS ─── */}
              {activeSection === 'skills' && (
                <>
                  {/* Languages */}
                  <div>
                    <label className={labelCls}>Languages *</label>
                    <div className="flex gap-1.5 sm:gap-2">
                      <input
                        type="text" value={newLanguage}
                        onChange={e => setNewLanguage(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(formData.languages, newLanguage, v => setFormData(p => ({ ...p, languages: v })), setNewLanguage); } }}
                        className={inputCls(!!errors.languages)} placeholder="Add a language (press Enter)"
                      />
                      <button type="button" onClick={() => addTag(formData.languages, newLanguage, v => setFormData(p => ({ ...p, languages: v })), setNewLanguage)}
                        className="px-4 sm:px-5 py-3 bg-primary-700 text-white rounded-xl font-bold text-xs sm:text-sm hover:bg-primary-800 transition-colors flex-shrink-0">
                        Add
                      </button>
                    </div>
                    {errors.languages && <p className="text-red-500 text-xs mt-1">{errors.languages}</p>}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.languages.map(lang => (
                        <span key={lang} className="flex items-center gap-1.5 bg-primary-100 text-primary-800 px-3 py-1.5 rounded-full text-sm font-medium">
                          {lang}
                          <button type="button" onClick={() => removeTag(formData.languages, lang, v => setFormData(p => ({ ...p, languages: v })))}
                            className="w-4 h-4 rounded-full bg-primary-300 hover:bg-primary-400 flex items-center justify-center text-primary-900 text-xs leading-none">×</button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Specialties */}
                  <div>
                    <label className={labelCls}>Specialties *</label>
                    <div className="flex gap-1.5 sm:gap-2">
                      <input
                        type="text" value={newSpecialty}
                        onChange={e => setNewSpecialty(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(formData.specialties, newSpecialty, v => setFormData(p => ({ ...p, specialties: v })), setNewSpecialty); } }}
                        className={inputCls(!!errors.specialties)} placeholder="Add a specialty (press Enter)"
                      />
                      <button type="button" onClick={() => addTag(formData.specialties, newSpecialty, v => setFormData(p => ({ ...p, specialties: v })), setNewSpecialty)}
                        className="px-4 sm:px-5 py-3 bg-primary-700 text-white rounded-xl font-bold text-xs sm:text-sm hover:bg-primary-800 transition-colors flex-shrink-0">
                        Add
                      </button>
                    </div>
                    {errors.specialties && <p className="text-red-500 text-xs mt-1">{errors.specialties}</p>}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.specialties.map(sp => (
                        <span key={sp} className="flex items-center gap-1.5 bg-amber-100 text-amber-800 px-3 py-1.5 rounded-full text-sm font-medium">
                          {sp}
                          <button type="button" onClick={() => removeTag(formData.specialties, sp, v => setFormData(p => ({ ...p, specialties: v })))}
                            className="w-4 h-4 rounded-full bg-amber-300 hover:bg-amber-400 flex items-center justify-center text-amber-900 text-xs leading-none">×</button>
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Certifications */}
                  <div>
                    <label className={labelCls}>Certifications</label>
                    <div className="flex gap-2">
                      <input
                        type="text" value={newCertification}
                        onChange={e => setNewCertification(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTag(formData.certifications, newCertification, v => setFormData(p => ({ ...p, certifications: v })), setNewCertification); } }}
                        className={inputCls()} placeholder="Add a certification (press Enter)"
                      />
                      <button type="button" onClick={() => addTag(formData.certifications, newCertification, v => setFormData(p => ({ ...p, certifications: v })), setNewCertification)}
                        className="px-4 sm:px-5 py-3 bg-primary-700 text-white rounded-xl font-bold text-xs sm:text-sm hover:bg-primary-800 transition-colors flex-shrink-0">
                        Add
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2 mt-3">
                      {formData.certifications.map(cert => (
                        <span key={cert} className="flex items-center gap-1.5 bg-blue-100 text-blue-800 px-3 py-1.5 rounded-full text-sm font-medium">
                          {cert}
                          <button type="button" onClick={() => removeTag(formData.certifications, cert, v => setFormData(p => ({ ...p, certifications: v })))}
                            className="w-4 h-4 rounded-full bg-blue-300 hover:bg-blue-400 flex items-center justify-center text-blue-900 text-xs leading-none">×</button>
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* ─── EMERGENCY ─── */}
              {activeSection === 'emergency' && (
                <>
                  <div className="bg-red-50 border border-red-100 rounded-2xl p-4 mb-2">
                    <p className="text-red-700 text-sm font-medium">🚨 Emergency contact details are used only in case of an emergency during a safari.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Contact Name *</label>
                      <input type="text" className={inputCls(!!errors.emergencyName)}
                        value={formData.emergencyContact.name}
                        onChange={e => setFormData(p => ({ ...p, emergencyContact: { ...p.emergencyContact, name: e.target.value } }))}
                        placeholder="Full name" />
                      {errors.emergencyName && <p className="text-red-500 text-xs mt-1">{errors.emergencyName}</p>}
                    </div>
                    <div>
                      <label className={labelCls}>Contact Phone *</label>
                      <input type="tel" className={inputCls(!!errors.emergencyPhone)}
                        value={formData.emergencyContact.phone}
                        onChange={e => setFormData(p => ({ ...p, emergencyContact: { ...p.emergencyContact, phone: e.target.value } }))}
                        placeholder="+254 7XX XXX XXX" />
                      {errors.emergencyPhone && <p className="text-red-500 text-xs mt-1">{errors.emergencyPhone}</p>}
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls}>Relationship *</label>
                      <input type="text" className={inputCls(!!errors.emergencyRelationship)}
                        value={formData.emergencyContact.relationship}
                        onChange={e => setFormData(p => ({ ...p, emergencyContact: { ...p.emergencyContact, relationship: e.target.value } }))}
                        placeholder="e.g. Spouse, Parent" />
                      {errors.emergencyRelationship && <p className="text-red-500 text-xs mt-1">{errors.emergencyRelationship}</p>}
                    </div>
                  </div>

                  <div className="mt-4 p-4 bg-gray-50 rounded-2xl">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-3">Quick navigation</p>
                    <div className="grid grid-cols-3 gap-2">
                      {sections.filter(s => s.id !== 'emergency').map(s => (
                        <button key={s.id} type="button" onClick={() => setActiveSection(s.id)}
                          className="flex flex-col items-center justify-center p-3 rounded-2xl bg-white border border-gray-100 text-[10px] font-bold text-gray-400 hover:text-primary-600 hover:border-primary-200 hover:bg-primary-50 transition-all shadow-sm">
                          <span className="text-lg mb-1">{s.icon}</span>
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-100 sm:rounded-b-3xl flex-shrink-0">
          <button
            type="button"
            onClick={() => {
              if (!guide && (formData.firstName || formData.lastName) && window.confirm('Discard unsaved changes?')) clearCache();
              onClose();
            }}
            className="flex-1 sm:flex-none px-4 sm:px-6 py-3 rounded-xl border border-gray-300 text-gray-700 font-bold text-xs sm:text-sm hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>

          <div className="flex items-center gap-3">
            {/* Section pills showing progress */}
            <div className="hidden sm:flex gap-1.5">
              {sections.map(s => (
                <div
                  key={s.id}
                  className={`w-2 h-2 rounded-full transition-all ${activeSection === s.id ? 'bg-primary-600 w-5' : 'bg-gray-300'}`}
                />
              ))}
            </div>

            <button
              type="submit"
              form="guide-form"
              disabled={loading}
              className="flex-1 sm:flex-none px-6 sm:px-8 py-3 rounded-xl bg-primary-700 hover:bg-primary-800 text-white font-bold text-xs sm:text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md active:scale-[0.98]"
            >
              {loading && (
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              )}
              {loading ? 'Saving...' : guide ? 'Update Guide' : 'Create Guide'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GuideForm;
